import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, Home, Sparkles } from 'lucide-react';
import { toast, ConfirmationModal } from '../../components/v2/V2Components';
import RoomService from '../../services/RoomService';
import HouseService from '../../services/HouseService';
import { sanitizeInput } from '../../utils/validation';
import { ROOM_FIELD_IDS } from '../../constants/formFieldIds';
import { FALLBACK_FLOOR_LEVELS, FALLBACK_ROOM_TYPES, reservedPropertyKeyError } from '../../constants/roomFormOptions';
import usePageView from '../../hooks/usePageView';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';

// Defined at module scope so the reference is stable across renders.
// Dirty-check compares JSON.stringify(formData) against this directly —
// no useRef needed since INITIAL_FORM_STATE never changes.
const INITIAL_FORM_STATE = {
    name: '',
    floor: '1st Floor',
    room_type: '',
    notes: '',
    properties: {}
};

// Config fallbacks live in constants/roomFormOptions.js, shared with
// RoomEdit so the two forms can't drift.

// Guards against legacy floor values absent from the fetched config list.
const withImportedOption = (options, currentValue) => {
    if (!currentValue) return options || [];
    const exists = options?.find(opt => opt.value === currentValue);
    if (!exists) {
        return [...(options || []), { value: currentValue, label: `${currentValue} (Legacy)` }];
    }
    return options || [];
};

const AddRoom = () => {
    usePageView(ANALYTICS_EVENTS.SPACES_ROOM_FORM_VIEWED, { is_edit_mode: false });
    const navigate = useNavigate();

    const [formData, setFormData] = useState(INITIAL_FORM_STATE);
    const [isDirty, setIsDirty] = useState(false);
    const [pendingDiscardDest, setPendingDiscardDest] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    // Inline save feedback — a toast alone disappears before the user finds
    // the offending field (ux-review: per-field errors were toast-only).
    const [nameError, setNameError] = useState(null);
    const [saveErrors, setSaveErrors] = useState([]);
    const [configOptions, setConfigOptions] = useState({
        floor_levels: FALLBACK_FLOOR_LEVELS,
        room_types: FALLBACK_ROOM_TYPES,
    });
    const [newPropKey, setNewPropKey] = useState('');
    const [newPropValue, setNewPropValue] = useState('');
    // House selection is deliberately NOT part of formData: the dirty-check
    // below compares formData against the module-level INITIAL_FORM_STATE, so
    // seeding the primary house into it would mark the form dirty on mount and
    // pop the discard-confirm modal on every Cancel.
    const [houses, setHouses] = useState([]);
    const [houseId, setHouseId] = useState('');

    // Fetch floor level config on mount; fall back to FALLBACK_FLOOR_LEVELS on error.
    useEffect(() => {
        let cancelled = false;
        RoomService.getConfig()
            .then(data => {
                if (!cancelled) {
                    const levels = data.floor_levels?.length ? data.floor_levels : FALLBACK_FLOOR_LEVELS;
                    const types = data.room_types?.length ? data.room_types : FALLBACK_ROOM_TYPES;
                    setConfigOptions({ floor_levels: levels, room_types: types });
                }
            })
            .catch(err => console.error('Failed to fetch room config:', err));
        return () => { cancelled = true; };
    }, []);

    // Fetch houses to populate the picker and default it to the primary house.
    //
    // allSettled, not all: the two requests are independent, and Promise.all
    // fails fast — a transient error on the primary-house lookup would leave
    // `houses` empty and hide the picker from a genuine multi-house user for
    // an unrelated reason. Each result is applied on its own. (Matches the
    // Promise.allSettled in RoomEdit's fetchRoom.) getPrimaryHouse resolves
    // null rather than throwing on 404, so `?.id` covers the no-primary case,
    // and the selection falls back to the first listed house so what the
    // control displays is always what gets submitted. If the houses call
    // itself fails the picker stays hidden and the backend still anchors new
    // rooms to the primary house on its own.
    useEffect(() => {
        let cancelled = false;
        Promise.allSettled([HouseService.getHouses(), HouseService.getPrimaryHouse()])
            .then(([listResult, primaryResult]) => {
                if (cancelled) return;

                const list = listResult.status === 'fulfilled' && Array.isArray(listResult.value)
                    ? listResult.value
                    : [];
                if (listResult.status === 'rejected') {
                    console.error('Failed to fetch houses:', listResult.reason);
                }
                setHouses(list);

                if (primaryResult.status === 'rejected') {
                    console.error('Failed to fetch primary house:', primaryResult.reason);
                }

                // Keep the displayed house and the submitted house in sync.
                // A <select> with value='' renders its FIRST option as
                // selected, so leaving houseId empty while the picker is
                // visible would show the user one house and then omit
                // house_id on save — the room silently lands wherever the
                // backend's own primary resolution puts it. Prefer the
                // resolved primary; otherwise fall back to the first listed
                // house, which is what the control is already showing.
                const primaryId = primaryResult.status === 'fulfilled' ? primaryResult.value?.id : null;
                const fallbackId = primaryId ?? list[0]?.id;
                if (fallbackId) setHouseId(String(fallbackId));
            });
        return () => { cancelled = true; };
    }, []);

    // Track form changes for dirty state — includes pending property draft inputs.
    useEffect(() => {
        setIsDirty(
            JSON.stringify(formData) !== JSON.stringify(INITIAL_FORM_STATE) ||
            newPropKey.trim() !== '' ||
            newPropValue.trim() !== ''
        );
    }, [formData, newPropKey, newPropValue]);

    // Handle browser close / refresh when dirty
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty]);

    const handleNavigation = (destination = '/spaces') => {
        if (isDirty) {
            setPendingDiscardDest(destination);
        } else {
            navigate(destination);
        }
    };

    const handleConfirmDiscard = () => {
        const dest = pendingDiscardDest;
        setPendingDiscardDest(null);
        setIsDirty(false);
        navigate(dest);
    };

    const handleCancelDiscard = () => {
        setPendingDiscardDest(null);
    };

    const handleChange = (field) => (e) => {
        setFormData(prev => ({ ...prev, [field]: e.target.value }));
    };

    const addProperty = () => {
        // Reserved keys collide with dedicated fields and become
        // invisible/unremovable in edit — shared guard with RoomEdit.
        const reservedError = reservedPropertyKeyError(newPropKey);
        if (reservedError) {
            toast.error(reservedError);
            return;
        }
        if (newPropKey.trim() && newPropValue.trim()) {
            setFormData(prev => ({
                ...prev,
                properties: {
                    ...prev.properties,
                    [sanitizeInput(newPropKey.trim(), 100)]: sanitizeInput(newPropValue.trim(), 255)
                }
            }));
            setNewPropKey('');
            setNewPropValue('');
        }
    };

    const removeProperty = (key) => {
        setFormData(prev => {
            const next = { ...prev.properties };
            delete next[key];
            return { ...prev, properties: next };
        });
    };

    const handleSave = async (e) => {
        e?.preventDefault();
        if (isSaving) return;
        if (!formData.name.trim()) {
            setNameError('Room Name is required');
            toast.error('Room Name is required');
            return;
        }
        // The pending property row bypasses addProperty() on direct submit,
        // so it needs the same reserved-key guard — otherwise typing "notes"
        // in the row and clicking Create silently merges a colliding key.
        const pendingReservedError = newPropValue.trim() ? reservedPropertyKeyError(newPropKey) : null;
        if (pendingReservedError) {
            toast.error(pendingReservedError);
            return;
        }
        setNameError(null);
        setSaveErrors([]);
        setIsSaving(true);
        try {
            // Committed properties are pre-sanitized in addProperty().
            // Only the pending row (bypasses addProperty) needs sanitizing here.
            const pendingProps = (newPropKey.trim() && newPropValue.trim())
                ? { [sanitizeInput(newPropKey.trim(), 100)]: sanitizeInput(newPropValue.trim(), 255) }
                : {};
            // notes lives inside the properties JSONB bag (RoomProperties.notes
            // in schemas/room.py); merge it in alongside any user-added custom
            // properties so the backend persists it without a schema change.
            const notes = formData.notes?.trim();
            const propertiesPayload = {
                ...formData.properties,
                ...pendingProps,
                ...(notes ? { notes: sanitizeInput(notes, 2000) } : {}),
            };
            const payload = {
                name: sanitizeInput(formData.name, 128),
                floor: sanitizeInput(formData.floor, 64),
                room_type: formData.room_type
                    ? sanitizeInput(formData.room_type, 32)
                    : null,
                properties: propertiesPayload,
                // Omit when unset so the backend applies its primary-house
                // default (api/rooms.py create_room) rather than receiving ''.
                ...(houseId ? { house_id: parseInt(houseId, 10) } : {}),
            };
            await RoomService.createRoom(payload);
            toast.success('Space created successfully');
            setIsDirty(false);
            navigate('/spaces');
        } catch (err) {
            console.error('Error creating room:', err);
            // Field-level Pydantic errors (loc/msg) render inline; the toast
            // carries the top-level message.
            setSaveErrors((err.errors || []).map((entry) =>
                `${(entry.loc || []).join('.') || 'form'}: ${entry.msg}`));
            toast.error(err.message || 'Failed to create space. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const inputClass = 'w-full p-3 rounded-xl bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-main)] placeholder-[var(--text-muted)] focus:ring-2 focus:ring-[var(--primary)] focus:outline-none transition-all';

    return (
        <div className="min-h-screen bg-[var(--bg-main)] text-[var(--text-main)] pb-24 md:pb-12 font-sans">
            <main className="max-w-6xl mx-auto px-6 md:px-12 mt-8 md:mt-12">

                {/* Page Header */}
                <div className="mb-8 p-1">
                    <span className="text-xs font-bold tracking-widest text-[var(--text-muted)] uppercase mb-2 block">
                        New Space
                    </span>
                    <h1 className="text-3xl md:text-4xl font-extrabold text-[var(--primary)] font-headline">
                        Add New Room
                    </h1>
                </div>

                <div className="flex flex-col lg:flex-row gap-8 md:gap-12 items-start">

                    {/* Left: Form */}
                    <div className="w-full lg:flex-[2] space-y-8">
                        <form onSubmit={handleSave} noValidate className="space-y-8">

                            {/* Core Fields */}
                            <div className="space-y-5">
                                {/* Room Name — full width */}
                                <div>
                                    <label htmlFor={ROOM_FIELD_IDS.name} className="block text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-1.5">
                                        Room Name <span className="text-[var(--error)]">*</span>
                                    </label>
                                    <input
                                        id={ROOM_FIELD_IDS.name}
                                        type="text"
                                        placeholder="e.g. Living Room"
                                        value={formData.name}
                                        onChange={handleChange('name')}
                                        maxLength={128}
                                        className={inputClass}
                                        onInput={() => nameError && setNameError(null)}
                                        aria-invalid={Boolean(nameError)}
                                    />
                                    {nameError && <p className="mt-1 text-xs text-red-500">{nameError}</p>}
                                </div>

                                {/* House — only shown for multi-house users.
                                    With a single house the backend's
                                    primary-house default is unambiguous, so
                                    the picker would be noise. */}
                                {houses.length > 1 && (
                                    <div>
                                        <label htmlFor={ROOM_FIELD_IDS.house} className="block text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-1.5">
                                            House
                                        </label>
                                        <select
                                            id={ROOM_FIELD_IDS.house}
                                            name="house_id"
                                            value={houseId}
                                            onChange={(e) => setHouseId(e.target.value)}
                                            className={inputClass}
                                        >
                                            {houses.map(h => (
                                                <option key={h.id} value={h.id}>{h.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    {/* Room Type */}
                                    <div>
                                        <label htmlFor={ROOM_FIELD_IDS.type} className="block text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-1.5">
                                            Room Type
                                        </label>
                                        <select
                                            id={ROOM_FIELD_IDS.type}
                                            value={formData.room_type}
                                            onChange={handleChange('room_type')}
                                            className={inputClass}
                                        >
                                            <option value="">Select a type...</option>
                                            {withImportedOption(configOptions.room_types, formData.room_type).map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Floor Level */}
                                    <div>
                                        <label htmlFor={ROOM_FIELD_IDS.floorLevel} className="block text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-1.5">
                                            Floor Level
                                        </label>
                                        <select
                                            id={ROOM_FIELD_IDS.floorLevel}
                                            value={formData.floor}
                                            onChange={handleChange('floor')}
                                            className={inputClass}
                                        >
                                            <option value="">Select a floor...</option>
                                            {withImportedOption(configOptions.floor_levels, formData.floor).map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Notes — persisted into properties.notes (RoomProperties schema) */}
                                <div>
                                    <label htmlFor={ROOM_FIELD_IDS.notes} className="block text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-1.5">
                                        Notes
                                    </label>
                                    <textarea
                                        id={ROOM_FIELD_IDS.notes}
                                        placeholder="Anything noteworthy about this room"
                                        value={formData.notes}
                                        onChange={handleChange('notes')}
                                        maxLength={2000}
                                        rows={3}
                                        className={inputClass}
                                    />
                                </div>
                            </div>

                            {/* Custom Properties */}
                            <div className="space-y-3">
                                <div>
                                    <p className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-1">
                                        Custom Properties
                                    </p>
                                    <p className="text-xs text-[var(--text-muted)]">
                                        Add any additional details like square footage, ceiling height, or permit numbers.
                                    </p>
                                </div>

                                {Object.entries(formData.properties).length > 0 && (
                                    <div className="space-y-2">
                                        {Object.entries(formData.properties).map(([key, value]) => (
                                            <div
                                                key={key}
                                                className="flex justify-between items-center bg-[var(--bg-hover)] px-4 py-2.5 rounded-xl text-sm"
                                            >
                                                <div className="flex-1 overflow-hidden">
                                                    <span className="font-medium text-[var(--text-main)]">{key}:</span>{' '}
                                                    <span className="text-[var(--text-secondary)]">{value}</span>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => removeProperty(key)}
                                                    className="text-[var(--text-muted)] hover:text-[var(--error)] ml-3 p-1 transition-colors"
                                                    aria-label={`Remove property ${key}`}
                                                    title={`Remove property ${key}`}
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="flex gap-2 items-center">
                                    <input
                                        aria-label="Property Name"
                                        placeholder="Property Name"
                                        value={newPropKey}
                                        onChange={(e) => setNewPropKey(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addProperty(); } }}
                                        maxLength={100}
                                        className="flex-1 p-3 rounded-xl bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-main)] text-sm placeholder-[var(--text-muted)] focus:ring-2 focus:ring-[var(--primary)] focus:outline-none transition-all"
                                    />
                                    <input
                                        aria-label="Property Value"
                                        placeholder="Value"
                                        value={newPropValue}
                                        onChange={(e) => setNewPropValue(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addProperty(); } }}
                                        maxLength={255}
                                        className="flex-1 p-3 rounded-xl bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-main)] text-sm placeholder-[var(--text-muted)] focus:ring-2 focus:ring-[var(--primary)] focus:outline-none transition-all"
                                    />
                                    <button
                                        type="button"
                                        onClick={addProperty}
                                        disabled={!newPropKey.trim() || !newPropValue.trim()}
                                        className="p-3 rounded-xl bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-main)] hover:bg-[var(--primary)] hover:text-white hover:border-[var(--primary)] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                        title="Add property"
                                    >
                                        <Plus size={16} />
                                    </button>
                                </div>
                            </div>

                            {saveErrors.length > 0 && (
                                <div className="p-4 bg-[var(--error-container)] text-[var(--error)] rounded-xl text-sm space-y-1" role="alert">
                                    {saveErrors.map((line) => <p key={line}>{line}</p>)}
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="pt-6 flex flex-col md:flex-row gap-4">
                                <button
                                    type="submit"
                                    disabled={isSaving}
                                    className="w-full md:w-auto px-10 py-4 bg-[var(--primary)] text-[var(--primary-text)] rounded-full font-bold text-lg hover:bg-[var(--primary-container)] transition-all shadow-xl shadow-[color-mix(in_srgb,var(--primary),transparent_90%)] flex items-center justify-center gap-2 disabled:opacity-70"
                                >
                                    {isSaving ? 'Creating...' : 'Create Room'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleNavigation()}
                                    disabled={isSaving}
                                    className="w-full md:w-auto px-10 py-4 bg-transparent text-[var(--secondary)] border border-[var(--border)] rounded-full font-bold text-lg hover:bg-[var(--bg-hover)] transition-all"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>

                        <p className="text-center md:text-left text-xs text-[var(--text-muted)] font-medium opacity-60 mt-4">
                            All data secured with 256-bit encryption
                        </p>
                    </div>

                    {/* Right: Contextual tip panel */}
                    <div className="w-full lg:flex-[1] order-first lg:order-last lg:sticky lg:top-8">
                        <div className="bg-[var(--bg-card)] rounded-2xl shadow-[var(--shadow-sm)] p-6 space-y-5">
                            <div className="w-10 h-10 rounded-full bg-[var(--primary-bg)] flex items-center justify-center">
                                <Home size={20} className="text-[var(--primary)]" />
                            </div>
                            <div>
                                <h3 className="font-headline font-bold text-lg text-[var(--text-main)] mb-2">
                                    Building Your House
                                </h3>
                                <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                                    Each room you add becomes a managed zone — you can track its inventory, maintenance history, paint colors, flooring, and more.
                                </p>
                            </div>

                            <div className="space-y-3 pt-2">
                                {[
                                    { title: 'Floor Level', detail: 'Group rooms by floor for easier navigation.' },
                                    { title: 'Wall Color', detail: 'Record paint colors for future touch-ups.' },
                                    { title: 'Custom Properties', detail: 'Add square footage, ceiling height, or anything else.' },
                                ].map(tip => (
                                    <div key={tip.title} className="flex gap-3 items-start">
                                        <div className="mt-0.5 w-5 h-5 rounded-full bg-[var(--primary-bg)] flex items-center justify-center flex-shrink-0">
                                            <Sparkles size={11} className="text-[var(--primary)]" />
                                        </div>
                                        <div>
                                            <p className="text-xs font-semibold text-[var(--text-main)]">{tip.title}</p>
                                            <p className="text-xs text-[var(--text-muted)] leading-relaxed">{tip.detail}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                </div>
            </main>

            <ConfirmationModal
                isOpen={pendingDiscardDest !== null}
                onClose={handleCancelDiscard}
                onConfirm={handleConfirmDiscard}
                title="Discard unsaved changes?"
                message="You have unsaved changes that will be lost if you leave this page."
                confirmText="Discard"
                cancelText="Keep editing"
                isDanger
            />
        </div>
    );
};

export default AddRoom;
